import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save, Upload, X, Image as ImageIcon, Type } from "lucide-react";
import api, { formatErr } from "../../lib/api";
import { uploadImage } from "../../lib/uploads";
import { useSite } from "../../contexts/SiteContext";

export default function DevBranding() {
  const { refresh } = useSite();
  const [s, setS] = useState({});

  useEffect(() => { api.get("/developer/settings").then(r => setS(r.data)); }, []);

  const save = async () => {
    try {
      const keys = [
        "site_name","tagline","logo_url","favicon_url","display_mode",
        "primary_color","accent_color","text_color","heading_color","link_color",
        "footer_bg_color","footer_text_color",
        "font_heading","font_body",
        "contact_email","contact_phone","contact_address",
        "instagram","facebook","twitter","youtube",
        "popup_enabled","popup_title","popup_body","popup_code","popup_image",
        "announcement_enabled","announcement_text",
      ];
      const patch = {}; keys.forEach(k => { if (s[k] !== undefined) patch[k] = s[k]; });
      await api.put("/developer/settings", patch);
      toast.success("Branding saved"); refresh();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const handleLogoFile = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (f.size > 2 * 1024 * 1024) return toast.error("Logo must be under 2 MB");
    try {
      const r = await uploadImage(f, "branding");
      setS((prev) => ({ ...prev, logo_url: r.url }));
      toast.success(r.storage === "s3" ? "Uploaded to S3 — click Save Branding to apply." : "Loaded — click Save Branding to apply.");
    } catch (err) { toast.error(formatErr(err)); }
  };

  const Field = ({ k, l, type = "text" }) => (
    <div>
      <div className="text-[11px] uppercase tracking-luxe text-white/40 mb-1">{l}</div>
      <input data-testid={`brand-${k}`} type={type} className="input-box" value={s[k] ?? ""} onChange={(e) => setS({ ...s, [k]: e.target.value })}/>
    </div>
  );

  const ColorField = ({ k, l }) => (
    <div>
      <div className="text-[11px] uppercase tracking-luxe text-white/40 mb-1">{l}</div>
      <div className="flex gap-2 items-center">
        <input
          type="color"
          data-testid={`brand-${k}-picker`}
          className="w-12 h-10 bg-transparent border border-white/15 cursor-pointer p-0"
          value={s[k] || "#000000"}
          onChange={(e) => setS({ ...s, [k]: e.target.value })}
        />
        <input
          data-testid={`brand-${k}`}
          type="text"
          className="input-box font-mono text-sm"
          placeholder="#0E0E0E"
          value={s[k] ?? ""}
          onChange={(e) => setS({ ...s, [k]: e.target.value })}
        />
      </div>
    </div>
  );

  const mode = s.display_mode || "name";

  return (
    <div className="space-y-6">
      <div><div className="overline mb-2">Identity</div><h1 className="font-display text-4xl">Branding & Site Settings</h1></div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Brand identity */}
        <div className="card-luxe p-6 space-y-4">
          <div className="overline">Brand</div>
          <Field k="site_name" l="Site Name"/>
          <Field k="tagline" l="Tagline"/>

          {/* Display mode toggle */}
          <div>
            <div className="text-[11px] uppercase tracking-luxe text-white/40 mb-2">Display Mode (Header)</div>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                data-testid="brand-display-name"
                onClick={() => setS({ ...s, display_mode: "name" })}
                className={`flex items-center justify-center gap-2 px-3 py-2.5 border text-[11px] uppercase tracking-luxe transition-colors ${mode === "name" ? "bg-copper text-white border-copper" : "border-white/15 text-white/70 hover:border-copper hover:text-white"}`}
              >
                <Type size={12}/> Brand Name
              </button>
              <button
                type="button"
                data-testid="brand-display-logo"
                onClick={() => setS({ ...s, display_mode: "logo" })}
                className={`flex items-center justify-center gap-2 px-3 py-2.5 border text-[11px] uppercase tracking-luxe transition-colors ${mode === "logo" ? "bg-copper text-white border-copper" : "border-white/15 text-white/70 hover:border-copper hover:text-white"}`}
              >
                <ImageIcon size={12}/> Logo Image
              </button>
            </div>
          </div>

          {/* Logo upload + URL field */}
          <div>
            <div className="text-[11px] uppercase tracking-luxe text-white/40 mb-1">Logo</div>
            <div className="flex items-center gap-3 mb-2">
              {s.logo_url ? (
                <div className="h-14 w-32 bg-white/5 border border-white/10 flex items-center justify-center overflow-hidden">
                  <img src={s.logo_url} alt="Logo preview" className="max-h-12 max-w-28 object-contain"/>
                </div>
              ) : (
                <div className="h-14 w-32 bg-white/5 border border-dashed border-white/15 flex items-center justify-center text-[10px] uppercase tracking-luxe text-white/30">No logo</div>
              )}
              <label data-testid="brand-logo-upload" className="cursor-pointer flex items-center gap-1.5 px-3 py-2 border border-white/15 hover:border-copper text-[11px] uppercase tracking-luxe text-white/70 hover:text-white">
                <Upload size={12}/> Upload
                <input type="file" accept="image/*" className="hidden" onChange={handleLogoFile}/>
              </label>
              {s.logo_url && (
                <button data-testid="brand-logo-clear" type="button" onClick={() => setS({ ...s, logo_url: "" })} className="text-[11px] uppercase tracking-luxe text-red-400 hover:text-red-300 flex items-center gap-1">
                  <X size={12}/> Clear
                </button>
              )}
            </div>
            <input data-testid="brand-logo_url" type="text" className="input-box" placeholder="Or paste an image URL" value={s.logo_url ?? ""} onChange={(e) => setS({ ...s, logo_url: e.target.value })}/>
            <div className="text-[10px] text-white/40 mt-1">PNG/SVG recommended. Max 2 MB. Active when Display Mode = Logo Image.</div>
          </div>

          <Field k="favicon_url" l="Favicon URL"/>
          <div>
            <div className="overline mb-2 mt-1">Brand Colours</div>
            <div className="grid grid-cols-2 gap-3">
              <ColorField k="primary_color" l="Primary"/>
              <ColorField k="accent_color" l="Accent"/>
              <ColorField k="heading_color" l="Heading Text"/>
              <ColorField k="text_color" l="Body Text"/>
              <ColorField k="link_color" l="Link / Copper"/>
              <ColorField k="footer_bg_color" l="Footer Background"/>
              <ColorField k="footer_text_color" l="Footer Text"/>
            </div>
            <div className="text-[10px] text-white/40 mt-2">Changes apply globally across the storefront after Save Branding. Dashboards keep their own theme.</div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field k="font_heading" l="Heading Font"/>
            <Field k="font_body" l="Body Font"/>
          </div>
        </div>

        {/* Contact / Social */}
        <div className="card-luxe p-6 space-y-4">
          <div className="overline">Contact</div>
          <Field k="contact_email" l="Email"/>
          <Field k="contact_phone" l="Phone"/>
          <Field k="contact_address" l="Address"/>
          <div className="overline pt-2">Social</div>
          <Field k="instagram" l="Instagram"/>
          <Field k="facebook" l="Facebook"/>
          <Field k="twitter" l="Twitter / X"/>
          <Field k="youtube" l="YouTube"/>
        </div>

        <div className="card-luxe p-6 space-y-4">
          <div className="overline">Announcement Bar</div>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!s.announcement_enabled} onChange={(e) => setS({ ...s, announcement_enabled: e.target.checked })}/> Show announcement bar</label>
          <Field k="announcement_text" l="Text"/>
        </div>

        <div className="card-luxe p-6 space-y-4">
          <div className="overline">Discount Popup</div>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!s.popup_enabled} onChange={(e) => setS({ ...s, popup_enabled: e.target.checked })}/> Show popup on first visit</label>
          <Field k="popup_title" l="Title"/>
          <div><div className="text-[11px] uppercase tracking-luxe text-white/40 mb-1">Body</div><textarea className="input-box" rows={3} value={s.popup_body || ""} onChange={(e) => setS({ ...s, popup_body: e.target.value })}/></div>
          <Field k="popup_code" l="Discount Code"/>
        </div>
      </div>
      <button data-testid="brand-save" onClick={save} className="btn-primary"><Save size={13}/> Save Branding</button>
    </div>
  );
}
